import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useInFormGrid } from '../../cmsPage/components/FormGrid';

export default function useFieldProps(props, shouldWatch = false) {
  const { fieldName, formContext, formFieldOptions, fieldWidth, value: providedValue } = props;
  // `fieldWidth` sizes a field on its own line; in a FormGrid column the grid sizes it.
  const inFormGrid = useInFormGrid();

  const inputAttributes = useMemo(
    () =>
      omit(props, [
        'className',
        'cols',
        'defaultValue',
        'fieldName',
        'fieldWidth',
        'filterFunction',
        'findValueInOptions',
        'fontWeight',
        'formContext',
        'formFieldOptions',
        // FieldWrapper's own (redesign iteration 1): not for the DOM or an MUI control.
        'help',
        'labelAside',
        // FormInput's control beside the box (SURF-4835).
        'after',
        'span',
        // DynamicFormField hands every field its tenant note (`html`, `htmlUnder`, read by
        // FieldWrapper for its margins) and the form a second time as `form` (read by the
        // option groups). On the DOM they became `html="<p>..."` and `form="[object Object]"`.
        'form',
        'html',
        'htmlUnder',
        'isDateOnly',
        'isSingleSelect',
        'label',
        'labelKey',
        'maxLength',
        'onBlur',
        'onChange',
        'options',
        'optionsLoader',
        'placeholder',
        'readOnly',
        'selectChange',
        'selectOptions',
        'sort',
        'sortTagsKey',
        'type',
        'valueKey',
        'variant',
      ]),
    [props]
  );

  const form = formContext || useFormContext();
  const errors = form?.formState?.errors || {};
  const value = shouldWatch ? form?.watch?.(fieldName) ?? providedValue : providedValue;

  // `get`, not `errors[fieldName]`: react-hook-form nests the errors of a dotted name
  // (`included.x`, `paramValues.y`) the way it nests their values, so such a field never
  // showed its error, and a grey Save had nothing red to point at (SURF-4889).
  const fieldError = get(errors, fieldName);

  const hasError = useMemo(() => !!fieldError, [fieldError]);
  const errorMessage = fieldError
    ? fieldError?.types
      ? Object.values(fieldError.types).join(' ')
      : fieldError.message || ''
    : '';

  const width = useMemo(() => {
    if (inFormGrid) return '100%';
    switch (fieldWidth) {
      case 'tiny': {
        return '8em';
      }
      case 'short': {
        return '16em';
      }
      case 'normal': {
        return '32em';
      }
      case 'long':
      case 'full': {
        return '100%';
      }
      default: {
        if (typeof fieldWidth === 'number') {
          return `${fieldWidth}px`; // Treat a number as a pixel value
        }
        if (![null, undefined, ''].includes(fieldWidth)) {
          return fieldWidth;
        }
      }
    }
    return '32em';
  }, [fieldWidth, inFormGrid]);
  // Either way of marking a field required counts: the rule the form checks, or a bare
  // `required` prop, which used to reach MUI alone and draw a marker of its own.
  const isRequired = useMemo(
    () => !!formFieldOptions?.required || !!props.required,
    [formFieldOptions, props.required]
  );

  return { width, inputAttributes, isRequired, value, fieldError, hasError, errorMessage };
}
